import { api } from "./api";
import type {
  Token,
  Email,
  Login,
  ResetConfirm,
  SignUpPayload,
  SignUpResponse,
  ActivationPayload,
  AnonKYCRequest,
} from "../../types/UserTypes";

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<Token, Login>({
      query: ({ code, ...rest }) => ({
        url: "/auth/login/",
        method: "POST",
        body: rest,
        headers: { "X-Archway-2fa": code },
      }),
      invalidatesTags: ["Users"],
    }),
    signup: builder.mutation<SignUpResponse, SignUpPayload>({
      query: (payload) => ({
        url: payload.query ? `/accounts/?${payload.query}` : "/accounts/",
        method: "POST",
        body: payload.body,
      }),
      invalidatesTags: ["Users"],
    }),
    activation: builder.mutation<Token, ActivationPayload>({
      query: (payload) => ({
        url: "/accounts/activation/",
        method: "POST",
        body: payload,
      }),
    }),
    resendActivation: builder.mutation<Email, Email>({
      query: (payload) => ({
        url: "/accounts/resend_activation/",
        method: "POST",
        body: payload,
      }),
    }),
    recovery: builder.mutation<Email, Email>({
      query: (payload) => ({
        url: "/accounts/reset_password/",
        method: "POST",
        body: payload,
      }),
    }),
    recoveryConfirm: builder.mutation<null, ResetConfirm>({
      query: (payload) => ({
        url: "/accounts/reset_password_confirm/",
        method: "POST",
        body: payload,
      }),
    }),
    logout: builder.mutation<null, null>({
      query: () => ({
        url: "/auth/logout/",
        method: "POST",
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Clear all data cache (nuclear!)
          dispatch(api.util.resetApiState());
        } catch (error) {
          console.error("Error during logout:", error);
        }
      },
    }),
    kyc: builder.mutation<Token, AnonKYCRequest>({
      query: (payload) => ({
        url: "/accounts/start_anon_verification/",
        method: "POST",
        body: payload,
      }),
    }),
    protected: builder.mutation<{ message: string }, void>({
      query: () => "protected",
    }),
  }),
});

export const {
  useLoginMutation,
  useSignupMutation,
  useProtectedMutation,
  useActivationMutation,
  useRecoveryMutation,
  useRecoveryConfirmMutation,
  useLogoutMutation,
  useResendActivationMutation,
  useKycMutation,
} = authApi;
